import React, {useState} from 'react';
import ProductModifierGroupQuantityInfo from "../ProductModifierGroupQuantityInfo";
import {ReactComponent as ArrowDownIco} from "../../../../../../../../images/icons/arrow-bottom.svg";
import {ReactComponent as ArrowUpIco} from "../../../../../../../../images/icons/arrow-top.svg";
import {Collapse} from "react-bootstrap";
import {useTranslation, withTranslation} from "react-i18next";
import ProductModifierGroupsModal from "../ProductModifierGroupsModal";
import ProductModifierGroupAdvancedOption from "./ProductModifierGroupAdvancedOption";
import StateItemHelper from "../../../../../../../services/Menu/services/State/StateItemHelper";
import {RootState} from "../../../../../../../../reducers";
import {getItems, getModifierGroups} from "../../../../../../../services/Menu/reducer";
import {getSession} from "../../../../../../../services/Session/reducer";
import {connect} from "react-redux";
import MenuEditItemViewHelper from "../../../../../../../services/Menu/services/ItemView/MenuEditItemViewHelper";

interface OwnProps {
    modifierGroup: Record<string, any>;
    updateProductState: (productState: Record<string, any>) => void;
    productState: Record<string, any>;
    isProductAvailable: boolean;
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

const ProductModifierGroupAdvanced = ({
                                          modifierGroup,
                                          productState,
                                          updateProductState,
                                          items,
                                          modifierGroups,
                                          session,
                                          isProductAvailable
                                      }: Props) => {
    const {t} = useTranslation();
    const [showItems, setShowItems] = useState(true);
    const [showModifierGroupsModal, setShowModifierGroupsModal] = useState(false);
    const selectedOptions = productState.modifier_groups.find((group: Record<string, any>) => group.modifier_group_id === modifierGroup.id)?.selected_items || [];

    const getFilteredSelectedOptions = () => {
        return selectedOptions
            .map((option: Record<string, any>) => {
                const modifierGroupOption = MenuEditItemViewHelper.parseOptionForIncludedQuantity(modifierGroup.options.find((opt: Record<string, any>) => opt.id === option.item_id));
                return {
                    ...option,
                    modifierGroupOption,
                };
            })
            .filter((option: Record<string, any>, index: number, array: Record<string, any>[]) => {
                return option.max_permitted !== 0 && !option.modifierGroupOption.only_included_and_cannot_modify && array.findIndex((arrayItem) => arrayItem.item_id === option.item_id) === index;
            })
    }

    const anySelectedOptions = getFilteredSelectedOptions().length > 0;

    const handleButtonClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        event.currentTarget.blur();
        setShowModifierGroupsModal(true)
    };

    const removeOption = (state: Record<string, any>, option: Record<string, any>, stateIndex: number) => {
        const newProductState = JSON.parse(JSON.stringify(productState));
        const modifierGroupIndex = newProductState.modifier_groups.findIndex((group: Record<string, any>) => group.modifier_group_id === modifierGroup.id);
        const selectedItemIndex = newProductState.modifier_groups[modifierGroupIndex].selected_items.findIndex((item: Record<string, any>) => item.item_id === state.item_id);
        newProductState.modifier_groups[modifierGroupIndex].selected_items.splice(selectedItemIndex + stateIndex, 1);
        updateProductState(newProductState);
    }

    const changeOptionQuantity = (quantity: number, option: Record<string, any>) => {
        const newProductState = JSON.parse(JSON.stringify(productState));
        StateItemHelper.setQuantityItem(newProductState, modifierGroup, option, quantity, items, modifierGroups, session.order_type);
        updateProductState(newProductState);
    }

    const adjustOption = (state: Record<string, any>, option: Record<string, any>, stateIndex: number) => {
        const newState = JSON.parse(JSON.stringify(state));
        const newInternalProductState = JSON.parse(JSON.stringify(productState));
        const modifierGroupIndex = newInternalProductState.modifier_groups.findIndex((group: Record<string, any>) => group.modifier_group_id === modifierGroup.id);
        const selectedItemIndex = newInternalProductState.modifier_groups[modifierGroupIndex].selected_items.findIndex((item: Record<string, any>) => item.item_id === state.item_id);
        newInternalProductState.modifier_groups[modifierGroupIndex].selected_items[selectedItemIndex + stateIndex] = newState;
        updateProductState(newInternalProductState);
    }

    const addSubOption = (option: Record<string, any>) => {
        const newProductState = JSON.parse(JSON.stringify(productState));
        StateItemHelper.setItemOption(newProductState, modifierGroup.id, option.id, 1, items, modifierGroups, (option.quantity ?? 0) + 1, session.order_type);
        updateProductState(newProductState);
    }

    if (!modifierGroup.adapted_max_permitted
        && modifierGroup.adapted_quantity >= modifierGroup.adapted_max_permitted
    ) return <></>;

    return (
        <fieldset className="form-group">
            <legend aria-live="polite">
                <div className="d-flex align-items-center justify-content-between mb-1">
                    <h5>
                        {modifierGroup.name}
                    </h5>
                    {anySelectedOptions && (
                        showItems ? <ArrowUpIco className="collapse-icon" onClick={() => setShowItems(false)}/> :
                            <ArrowDownIco className="collapse-icon" onClick={() => setShowItems(true)}/>
                    )}
                </div>
                <ProductModifierGroupQuantityInfo
                    hideBadge={isProductAvailable ? undefined : true}
                    quantity={modifierGroup.adapted_quantity}
                    quantityInfo={{
                        minPermitted: modifierGroup.adapted_min_permitted,
                        maxPermitted: modifierGroup.adapted_max_permitted,
                        chargeAbove: modifierGroup.adapted_charge_above
                    }}/>
            </legend>
            <Collapse in={showItems}>
                <div>
                    <div className="d-flex flex-column gap-2">
                        {
                            getFilteredSelectedOptions().map((option: Record<string, any>) => {
                                const {modifierGroupOption} = option;

                                if (modifierGroupOption.min_permitted && modifierGroupOption.quantity <= modifierGroupOption.min_permitted) {
                                    return <></>;
                                }

                                return (
                                    <div key={`${modifierGroup.id}-${option.item_id}`} aria-live="polite">
                                        <ProductModifierGroupAdvancedOption modifierGroupOption={modifierGroupOption}
                                                                            adjustOption={(optionState: Record<string, any>, stateIndex: number) => adjustOption(optionState, modifierGroupOption, stateIndex)}
                                                                            handleAddSubOption={addSubOption}
                                                                            removeOption={(optionState: Record<string, any>, stateIndex: number) => removeOption(optionState, option, stateIndex)}
                                                                            modifierGroup={modifierGroup}
                                                                            changeOptionQuantity={changeOptionQuantity}
                                        />
                                    </div>
                                )
                            })
                        }
                        <button disabled={!isProductAvailable}
                                className={`btn ${anySelectedOptions ? "btn-default" : "btn-primary"}`}
                                onClick={handleButtonClick}>
                            {anySelectedOptions ? t("modules.menu.action.change_choice.title") : t("common.action.choose")}
                        </button>
                    </div>
                </div>
            </Collapse>
            {
                showModifierGroupsModal && (
                    <ProductModifierGroupsModal show={showModifierGroupsModal}
                                                onHide={() => setShowModifierGroupsModal(false)}
                                                modifierGroupId={modifierGroup.id}
                                                updateProductState={updateProductState}
                                                productState={productState}/>
                )
            }
        </fieldset>
    )
}

const mapStateToProps = (state: RootState) => ({
    items: getItems(state.menu),
    modifierGroups: getModifierGroups(state.menu),
    session: getSession(state.session),
})

const connector = connect(mapStateToProps);
export default withTranslation()(connector(ProductModifierGroupAdvanced));
